import { useCallback, useMemo } from 'react';
import UIButton from 'UIComponents/button/UIButton';
import FormattedMessage from 'I18n/components/FormattedMessage';
import useCrmObjectTypeListingFilters from 'framework-listing-lib/hooks/useCrmObjectTypeListingFilters';
import { FOLDER_ID } from 'framework-listing-lib/internal/Folders/constants/folders';
import useQuickFilters from 'framework-listing-lib/hooks/useQuickFilters';
import useCrmObjectTypeTracking from 'framework-listing-lib/internal/hooks/useCrmObjectTypeTracking';
import { useFilterGroups, useHiddenFilter } from 'framework-listing-lib/hooks/useListingLibFilters';
import { useCurrentViewFilters } from 'framework-listing-lib/hooks/useCurrentView';
import { useHasViews } from '../../ViewTabs/hooks/useViewProps';
import { areSameFilterGroups, areSameFilterOperators, removeFilterGroups } from 'framework-listing-lib/utils/filters';
import { BU_PROPERTY_NAME } from 'framework-listing-lib/constants/businessUnits';
import { useHasBUNavIntegration } from 'framework-listing-lib/internal/frameworkAppSettings/hooks/useFrameworkAppSettings';
import { jsx as _jsx } from "react/jsx-runtime";
export default function ClearAllFiltersButton() {
  const {
    onClearFilters,
    hasAppliedFilters,
    isFilterFromCustomView,
    initialFilter
  } = useCrmObjectTypeListingFilters();
  const hasViews = useHasViews();
  const currentViewFilters = useCurrentViewFilters();
  const filterGroups = useFilterGroups();
  const quickFilters = useQuickFilters();
  const hiddenFilter = useHiddenFilter();
  const {
    trackCrmTableInteraction
  } = useCrmObjectTypeTracking();
  const hasBUNavIntegration = useHasBUNavIntegration();
  const shouldRenderClearFilterButton = useMemo(() => {
    if (!hasAppliedFilters && hasViews || isFilterFromCustomView) {
      return false;
    }

    /**
     * Extract hs_folder_id filter to prevent button form rendering when filtering only by hs_folder_id(folders mode).
     * When hasBUNavIntegration is true, BU filters will not appear here.
     */
    const currentFilters = filterGroups.map(filterGroup => Object.assign({}, filterGroup, {
      filters: filterGroup.filters.filter(filter => filter.property !== FOLDER_ID)
    })).filter(filterGroup => filterGroup.filters.length > 0);

    /**
     * Extract BU_PROPERTY_NAME filter when hasBUNavIntegration is true since there are views from the past that have the BU property still saved.
     * BU filter is now applied through hidden filters and it wont appear in filterGroups.
     */
    const viewFiltersWithoutBU = hasBUNavIntegration ? currentViewFilters.filterGroups.map(filterGroup => Object.assign({}, filterGroup, {
      filters: filterGroup.filters.filter(filter => filter.property !== BU_PROPERTY_NAME)
    })).filter(filterGroup => filterGroup.filters.length > 0) : currentViewFilters.filterGroups;

    /**
     * View filters may contain the hiddenFilter that was active the last time
     * the view was saved. We remove this from our filterGroups context state
     * when the view is loaded, so we also need to remove it here
     */
    const viewFiltersWithoutHiddenFilter = removeFilterGroups(viewFiltersWithoutBU, hiddenFilter.filterGroups);

    /**
     * Extract BU_PROPERTY_NAME filter when hasBUNavIntegration is true since there are views from the past that have the BU property still saved.
     * BU filter is now applied through hidden filters and it wont appear in filterGroups.
     */
    const viewQuickFilters = hasBUNavIntegration ? currentViewFilters.quickFilters.filter(filter => filter.property !== BU_PROPERTY_NAME) : currentViewFilters.quickFilters;

    /**
     * Filters applied by views are our "zero state". When the clear all views button is clicked,
     * all filters are removed except for view filters. If the current views are not the same as
     * the view filters, we should render the clear all button.
     */

    if (!areSameFilterOperators(quickFilters, viewQuickFilters)) {
      return true;
    }
    const initialFilterGroups = (initialFilter === null || initialFilter === void 0 ? void 0 : initialFilter.filterGroups) || [];
    const currentInitialFilters = initialFilterGroups.map(filterGroup => Object.assign({}, filterGroup, {
      filters: filterGroup.filters.filter(filter => filter.property !== FOLDER_ID)
    })).filter(filterGroup => filterGroup.filters.length > 0);
    if (!hasViews) {
      return !areSameFilterGroups(currentFilters, currentInitialFilters);
    }
    if (!areSameFilterGroups(currentFilters, viewFiltersWithoutHiddenFilter)) {
      return true;
    }
    return false;
  }, [currentViewFilters.filterGroups, currentViewFilters.quickFilters, filterGroups, hasAppliedFilters, hasBUNavIntegration, hasViews, initialFilter === null || initialFilter === void 0 ? void 0 : initialFilter.filterGroups, hiddenFilter.filterGroups, isFilterFromCustomView, quickFilters]);
  const handleClearAllClick = useCallback(() => {
    trackCrmTableInteraction({
      action: 'click-clear-all-button'
    });
    onClearFilters();
  }, [onClearFilters, trackCrmTableInteraction]);
  if (!shouldRenderClearFilterButton) {
    return null;
  }
  return /*#__PURE__*/_jsx(UIButton, {
    "data-test-id": "crm-listing-clear-all-filters-button",
    use: "transparent",
    onClick: handleClearAllClick,
    children: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "frameworkListingLib.filterBar.clearAll"
    })
  });
}